import { computed, inject, provide, ref } from 'vue';
import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { BlockRect, InitPayload } from './protocol';
import type { BlockNode, BuilderBreakpoint } from './types';
import { markerId } from './types';
import { canPlace, canWrapInSection, SECTION_SLUG, slotCountOf } from './nestingRules';

/**
 * Builder v2 shell state — one store per builder instance (provide/inject,
 * no Pinia). Every mutation goes through commit(), which snapshots the
 * tree for the client-side undo stack (cap 50, typing coalesced 800 ms).
 * Mutations never materialize sparse keys and never touch existing ids —
 * the backward-compat contract lives in the data it leaves alone.
 */
/** Where a value edit originated; steers the shell's patch loop. */
export type MutationOrigin = 'inline' | 'document';

export interface BuilderStore {
    content: Ref<BlockNode[]>;
    /** Multi-selection in document order of clicks; primary = last element. */
    selectedIds: Ref<string[]>;
    /** Primary selection facade over selectedIds (single-select consumers). */
    selectedId: Ref<string | null>;
    hoverId: Ref<string | null>;
    bridgeReady: Ref<boolean>;
    rects: Ref<Record<string, BlockRect>>;
    documentHeight: Ref<number>;
    labels: Ref<Record<string, string>>;
    /** Inline-editable attribute map per block id, mirrored to the bridge init. */
    inline: Ref<NonNullable<InitPayload['inline']>>;
    /** Table-matrix attribute per block id, mirrored to the bridge init. */
    tables: Ref<NonNullable<InitPayload['tables']>>;
    /** Bumped when a dblclick routes to the panel — BlockPanel focuses its first field. */
    panelFocusSerial: Ref<number>;
    mode: Ref<'edit' | 'readonly'>;
    /** Active canvas breakpoint — pure UI state, never part of content/undo. */
    breakpoint: Ref<BuilderBreakpoint>;
    /** Canvas scale factor (scaled iframe fit) — parent↔iframe coordinate bridge. */
    canvasScale: Ref<number>;
    dirty: Ref<boolean>;
    /** Bumped on every mutation; `kind` steers patchLevel vs patchRegion.
     *  `origin: 'inline'` marks canvas value edits whose iframe DOM is already
     *  correct; `origin: 'document'` marks document-surface edits (no canvas). */
    lastMutation: Ref<{ kind: 'value' | 'structure'; id: string | null; serial: number; origin?: MutationOrigin } | null>;
    canUndo: ComputedRef<boolean>;
    canRedo: ComputedRef<boolean>;
    select(id: string | null): void;
    toggleSelect(id: string): void;
    rangeSelect(id: string): void;
    hover(id: string | null): void;
    findBlock(id: string): BlockNode | null;
    updateValue(id: string, attribute: string, value: unknown, origin?: MutationOrigin): boolean;
    /** Sparse visibility contract: hidden = false key, visible = key absence. */
    setVisibility(id: string, breakpoint: BuilderBreakpoint, visible: boolean): boolean;
    /** Outline eye toggle — hidden blocks vanish from render entirely. */
    toggleHidden(id: string): boolean;
    duplicateBlock(id: string): string | null;
    removeBlock(id: string): boolean;
    moveBlock(id: string, direction: -1 | 1): boolean;
    insertAfter(id: string | null, block: BlockNode): string | null;
    /** Move a block into a parent slot, before a rendered sibling (null = append). */
    moveTo(id: string, parentId: string | null, slot: number, beforeId: string | null): boolean;
    /** Insert a new block at a drop target (palette drag). */
    insertAt(block: BlockNode, parentId: string | null, slot: number, beforeId: string | null): string | null;
    /** Paste cloned subtrees at a target; ids regenerated, canPlace-gated (one undo step). */
    pasteAt(blocks: BlockNode[], parentId: string | null, slot: number, beforeId: string | null): string[];
    /** Swap a block for a new one in place (slash / markdown conversions). */
    replaceBlock(id: string, block: BlockNode): string | null;
    /** Wrap root-level blocks into a new Section (one undo step). */
    wrapInSection(ids: string[]): string | null;
    /** Change a container's slot-count attribute and fold orphans (one undo step). */
    setSlotCount(id: string, attribute: string, count: number): boolean;
    removeBlocks(ids: string[]): boolean;
    duplicateBlocks(ids: string[]): string[];
    undo(): boolean;
    redo(): boolean;
}

const STORE_KEY: InjectionKey<BuilderStore> = Symbol('builder-v2-store');

const UNDO_CAP = 50;
const COALESCE_MS = 800;

/** Random block id ('l_' + 10 chars base36) for NEW blocks only. */
export function newBlockId(): string {
    const bytes = new Uint8Array(10);

    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    return 'l_' + Array.from(bytes, (byte) => (byte % 36).toString(36)).join('');
}

/** Pure store factory — unit-testable without a component context. */
export function makeBuilderStore(initialContent: BlockNode[]): BuilderStore {
    const content = ref<BlockNode[]>(initialContent);
    const selectedIds = ref<string[]>([]);
    const selectedId = computed<string | null>({
        get: () => (selectedIds.value.length ? selectedIds.value[selectedIds.value.length - 1] : null),
        set: (id) => {
            selectedIds.value = id === null ? [] : [id];
        },
    });
    const hoverId = ref<string | null>(null);
    const mode = ref<'edit' | 'readonly'>('edit');
    const dirty = ref(false);
    const lastMutation = ref<BuilderStore['lastMutation']['value']>(null);

    const undoStack = ref<BlockNode[][]>([]);
    const redoStack = ref<BlockNode[][]>([]);
    let serial = 0;
    let lastCommitLabel: string | null = null;
    let lastCommitAt = 0;

    const clone = (tree: BlockNode[]): BlockNode[] => JSON.parse(JSON.stringify(tree)) as BlockNode[];

    const record = (kind: 'value' | 'structure', id: string | null, origin?: MutationOrigin): void => {
        dirty.value = true;
        lastMutation.value = origin ? { kind, id, serial: ++serial, origin } : { kind, id, serial: ++serial };
    };

    const commit = (label: string, mutate: () => boolean): boolean => {
        if (mode.value === 'readonly') {
            return false;
        }

        const snapshot = clone(content.value);
        const now = Date.now();
        const coalesce = label === lastCommitLabel && now - lastCommitAt < COALESCE_MS;

        if (!mutate()) {
            return false;
        }

        if (!coalesce) {
            undoStack.value.push(snapshot);

            if (undoStack.value.length > UNDO_CAP) {
                undoStack.value.shift();
            }
        }

        redoStack.value = [];
        lastCommitLabel = label;
        lastCommitAt = now;

        return true;
    };

    /** Parent list + index of a block anywhere in the tree. */
    const locate = (blocks: BlockNode[], id: string): { list: BlockNode[]; index: number } | null => {
        for (let index = 0; index < blocks.length; index++) {
            if (markerId(blocks[index]) === id) {
                return { list: blocks, index };
            }

            const children = blocks[index].children;

            if (children?.length) {
                const found = locate(children, id);

                if (found) {
                    return found;
                }
            }
        }

        return null;
    };

    const regenerateIds = (block: BlockNode): BlockNode => ({
        ...block,
        id: newBlockId(),
        ...(block.children?.length ? { children: block.children.map(regenerateIds) } : {}),
    });

    /** Block ids in document (DFS) order — the outline's visual order. */
    const flattenIds = (blocks: BlockNode[]): string[] => {
        const out: string[] = [];

        const visit = (list: BlockNode[]): void => {
            for (const block of list) {
                const id = markerId(block);

                if (id !== null) {
                    out.push(id);
                }

                if (block.children?.length) {
                    visit(block.children);
                }
            }
        };

        visit(blocks);

        return out;
    };

    /** Whether `id` is the block itself or lives anywhere in its subtree. */
    const inSubtree = (block: BlockNode, id: string): boolean => {
        if (markerId(block) === id) {
            return true;
        }

        return (block.children ?? []).some((child) => inSubtree(child, id));
    };

    /** Reduce a selection to blocks with no selected ancestor, document order. */
    const topLevelIds = (ids: string[]): string[] => {
        const idSet = new Set(ids);
        const result: string[] = [];

        const visit = (blocks: BlockNode[], underSelected: boolean): void => {
            for (const block of blocks) {
                const id = markerId(block);
                const selected = id !== null && idSet.has(id);

                if (selected && !underSelected) {
                    result.push(id as string);
                }

                if (block.children?.length) {
                    visit(block.children, underSelected || selected);
                }
            }
        };

        visit(content.value, false);

        return result;
    };

    /** Drop selection entries that no longer resolve to a block. */
    const pruneSelection = (): void => {
        selectedIds.value = selectedIds.value.filter((id) => locate(content.value, id) !== null);
    };

    /** Resolve a drop parent; `undefined` = invalid target, `null` = root. */
    const resolveParent = (parentId: string | null): BlockNode | null | undefined => {
        if (parentId === null) {
            return null;
        }

        const found = locate(content.value, parentId);

        return found ? found.list[found.index] : undefined;
    };

    /** Flat insertion index for a drop — before a sibling id, or list end. */
    const insertionIndex = (list: BlockNode[], beforeId: string | null): number => {
        if (beforeId === null) {
            return list.length;
        }

        const index = list.findIndex((block) => markerId(block) === beforeId);

        return index === -1 ? list.length : index;
    };

    /** Sparse slot contract: slot 0 is expressed by key absence. */
    const applySlot = (block: BlockNode, slot: number): void => {
        if (slot === 0) {
            delete block.slot;
            return;
        }

        block.slot = slot;
    };

    const store: BuilderStore = {
        content,
        selectedIds,
        selectedId,
        hoverId,
        bridgeReady: ref(false),
        rects: ref({}),
        documentHeight: ref(0),
        labels: ref({}),
        inline: ref({}),
        tables: ref({}),
        panelFocusSerial: ref(0),
        mode,
        breakpoint: ref<BuilderBreakpoint>('desktop'),
        canvasScale: ref(1),
        dirty,
        lastMutation,
        canUndo: computed(() => undoStack.value.length > 0),
        canRedo: computed(() => redoStack.value.length > 0),
        select(id) {
            selectedId.value = id;
        },
        toggleSelect(id) {
            if (selectedIds.value.includes(id)) {
                selectedIds.value = selectedIds.value.filter((existing) => existing !== id);
                return;
            }

            selectedIds.value = [...selectedIds.value, id];
        },
        rangeSelect(id) {
            const anchor = selectedId.value;

            if (anchor === null || anchor === id) {
                selectedIds.value = [id];
                return;
            }

            const flat = flattenIds(content.value);
            const anchorIndex = flat.indexOf(anchor);
            const targetIndex = flat.indexOf(id);

            if (anchorIndex === -1 || targetIndex === -1) {
                selectedIds.value = [id];
                return;
            }

            const range =
                anchorIndex < targetIndex
                    ? flat.slice(anchorIndex, targetIndex + 1)
                    : flat.slice(targetIndex, anchorIndex + 1).reverse();

            selectedIds.value = range;
        },
        hover(id) {
            hoverId.value = id;
        },
        findBlock(id) {
            const found = locate(content.value, id);

            return found ? found.list[found.index] : null;
        },
        updateValue(id, attribute, value, origin) {
            const applied = commit(`value:${id}:${attribute}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const block = found.list[found.index];
                block.value = { ...(block.value ?? {}), [attribute]: value };

                return true;
            });

            if (applied) {
                record('value', id, origin);
            }

            return applied;
        },
        setVisibility(id, breakpoint, visible) {
            // state in the label: opposite toggles alternate labels → no coalescing
            const applied = commit(`visibility:${id}:${breakpoint}:${visible}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const block = found.list[found.index];
                const visibility = { ...(block.visibility ?? {}) };

                if (visible) {
                    delete visibility[breakpoint];
                } else {
                    visibility[breakpoint] = false;
                }

                if (Object.keys(visibility).length === 0) {
                    delete block.visibility;
                } else {
                    block.visibility = visibility;
                }

                return true;
            });

            if (applied) {
                record('value', id);
            }

            return applied;
        },
        toggleHidden(id) {
            const applied = commit(`hidden:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const block = found.list[found.index];

                if (block.hidden) {
                    delete block.hidden;
                } else {
                    block.hidden = true;
                }

                return true;
            });

            if (applied) {
                record('structure', id);
            }

            return applied;
        },
        duplicateBlock(id) {
            let newId: string | null = null;

            const applied = commit(`duplicate:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const copy = regenerateIds(clone([found.list[found.index]])[0]);
                newId = markerId(copy);
                found.list.splice(found.index + 1, 0, copy);

                return true;
            });

            if (applied) {
                record('structure', newId);
            }

            return applied ? newId : null;
        },
        removeBlock(id) {
            const applied = commit(`remove:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                found.list.splice(found.index, 1);

                return true;
            });

            if (applied) {
                record('structure', null);
                pruneSelection();
            }

            return applied;
        },
        moveBlock(id, direction) {
            const applied = commit(`move:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const target = found.index + direction;

                if (target < 0 || target >= found.list.length) {
                    return false;
                }

                const [block] = found.list.splice(found.index, 1);
                found.list.splice(target, 0, block);

                return true;
            });

            if (applied) {
                record('structure', id);
            }

            return applied;
        },
        insertAfter(id, block) {
            const inserted = { ...block, id: block.id ?? newBlockId() };
            const insertedId = markerId(inserted);

            const applied = commit('insert', () => {
                if (id === null) {
                    content.value.push(inserted);

                    return true;
                }

                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                found.list.splice(found.index + 1, 0, inserted);

                return true;
            });

            if (applied) {
                record('structure', insertedId);
                selectedId.value = insertedId;
            }

            return applied ? insertedId : null;
        },
        moveTo(id, parentId, slot, beforeId) {
            const applied = commit(`moveTo:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const block = found.list[found.index];

                if (parentId !== null && inSubtree(block, parentId)) {
                    return false;
                }

                const parent = resolveParent(parentId);

                if (parent === undefined || !canPlace(block, parent)) {
                    return false;
                }

                const targetSlot = parent === null ? 0 : slot;

                if (parent !== null && (targetSlot < 0 || targetSlot >= slotCountOf(parent))) {
                    return false;
                }

                found.list.splice(found.index, 1);

                const list = parent === null ? content.value : (parent.children ??= []);
                applySlot(block, targetSlot);
                list.splice(insertionIndex(list, beforeId), 0, block);

                return true;
            });

            if (applied) {
                record('structure', id);
            }

            return applied;
        },
        insertAt(block, parentId, slot, beforeId) {
            const inserted = { ...block, id: block.id ?? newBlockId() };
            const insertedId = markerId(inserted);

            const applied = commit('insert', () => {
                const parent = resolveParent(parentId);

                if (parent === undefined || !canPlace(inserted, parent)) {
                    return false;
                }

                const targetSlot = parent === null ? 0 : slot;

                if (parent !== null && (targetSlot < 0 || targetSlot >= slotCountOf(parent))) {
                    return false;
                }

                const list = parent === null ? content.value : (parent.children ??= []);
                applySlot(inserted, targetSlot);
                list.splice(insertionIndex(list, beforeId), 0, inserted);

                return true;
            });

            if (applied) {
                record('structure', insertedId);
                selectedId.value = insertedId;
            }

            return applied ? insertedId : null;
        },
        pasteAt(blocks, parentId, slot, beforeId) {
            const pasted = blocks.map((block) => regenerateIds(clone([block])[0]));
            const pastedIds = pasted.map(markerId).filter((id): id is string => id !== null);

            const applied = commit('paste', () => {
                const parent = resolveParent(parentId);

                if (parent === undefined || pasted.length === 0 || !pasted.every((block) => canPlace(block, parent))) {
                    return false;
                }

                const targetSlot = parent === null ? 0 : slot;

                if (parent !== null && (targetSlot < 0 || targetSlot >= slotCountOf(parent))) {
                    return false;
                }

                const list = parent === null ? content.value : (parent.children ??= []);
                const index = insertionIndex(list, beforeId);

                for (const block of pasted) {
                    applySlot(block, targetSlot);
                }

                list.splice(index, 0, ...pasted);

                return true;
            });

            if (applied) {
                record('structure', null);
                selectedIds.value = pastedIds;
            }

            return applied ? pastedIds : [];
        },
        replaceBlock(id, block) {
            const replacement = { ...block, id: block.id ?? newBlockId() };
            const replacementId = markerId(replacement);

            const applied = commit(`replace:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const original = found.list[found.index];

                if (original.slot !== undefined) {
                    replacement.slot = original.slot;
                }

                found.list.splice(found.index, 1, replacement);

                return true;
            });

            if (applied) {
                record('structure', replacementId);
                selectedId.value = replacementId;
            }

            return applied ? replacementId : null;
        },
        wrapInSection(ids) {
            let sectionId: string | null = null;

            const applied = commit('wrapInSection', () => {
                const targets = topLevelIds(ids);

                if (targets.length === 0 || !canWrapInSection(content.value, targets)) {
                    return false;
                }

                const idSet = new Set(targets);
                const isWrapped = (block: BlockNode): boolean => {
                    const blockId = markerId(block);

                    return blockId !== null && idSet.has(blockId);
                };

                const gathered = content.value.filter(isWrapped);
                const firstIndex = content.value.findIndex(isWrapped);

                if (gathered.length !== targets.length || firstIndex === -1) {
                    return false;
                }

                for (const block of gathered) {
                    delete block.slot;
                }

                const section: BlockNode = {
                    id: newBlockId(),
                    type: SECTION_SLUG,
                    useBlock: false,
                    value: {},
                    settings: {},
                    children: gathered,
                };

                sectionId = markerId(section);
                const remaining = content.value.filter((block) => !isWrapped(block));
                remaining.splice(firstIndex, 0, section);
                content.value = remaining;

                return true;
            });

            if (applied) {
                record('structure', sectionId);
                selectedId.value = sectionId;
            }

            return applied ? sectionId : null;
        },
        setSlotCount(id, attribute, count) {
            const applied = commit(`slotCount:${id}`, () => {
                const found = locate(content.value, id);

                if (!found) {
                    return false;
                }

                const block = found.list[found.index];
                const next = Math.max(1, Math.min(6, Math.trunc(count)));
                block.value = { ...(block.value ?? {}), [attribute]: next };

                for (const child of block.children ?? []) {
                    const childSlot = Math.max(0, Math.trunc(Number(child.slot ?? 0)));

                    if (childSlot >= next) {
                        applySlot(child, next - 1);
                    }
                }

                return true;
            });

            if (applied) {
                record('structure', id);
            }

            return applied;
        },
        removeBlocks(ids) {
            const applied = commit('removeBlocks', () => {
                const targets = topLevelIds(ids);

                if (targets.length === 0) {
                    return false;
                }

                for (const target of targets) {
                    const found = locate(content.value, target);

                    if (found) {
                        found.list.splice(found.index, 1);
                    }
                }

                return true;
            });

            if (applied) {
                record('structure', null);
                pruneSelection();
            }

            return applied;
        },
        duplicateBlocks(ids) {
            const newIds: string[] = [];

            const applied = commit('duplicateBlocks', () => {
                const targets = topLevelIds(ids);

                if (targets.length === 0) {
                    return false;
                }

                for (const target of targets) {
                    const found = locate(content.value, target);

                    if (!found) {
                        continue;
                    }

                    const copy = regenerateIds(clone([found.list[found.index]])[0]);
                    const copyId = markerId(copy);

                    found.list.splice(found.index + 1, 0, copy);

                    if (copyId !== null) {
                        newIds.push(copyId);
                    }
                }

                return newIds.length > 0;
            });

            if (applied) {
                record('structure', null);
                selectedIds.value = newIds;
            }

            return applied ? newIds : [];
        },
        undo() {
            const snapshot = undoStack.value.pop();

            if (!snapshot) {
                return false;
            }

            redoStack.value.push(clone(content.value));
            content.value = snapshot;
            lastCommitLabel = null;
            record('structure', null);
            pruneSelection();

            return true;
        },
        redo() {
            const snapshot = redoStack.value.pop();

            if (!snapshot) {
                return false;
            }

            undoStack.value.push(clone(content.value));
            content.value = snapshot;
            lastCommitLabel = null;
            record('structure', null);
            pruneSelection();

            return true;
        },
    };

    return store;
}

export function createBuilderStore(content: BlockNode[]): BuilderStore {
    const store = makeBuilderStore(content);

    provide(STORE_KEY, store);

    return store;
}

export function useBuilderStore(): BuilderStore {
    const store = inject(STORE_KEY);

    if (!store) {
        throw new Error('useBuilderStore must be used inside a BuilderV2 tree');
    }

    return store;
}
